import type { Breadcrumb } from '@bugsnag/js';

import { getBuildConfig } from 'app-components/utils/core';
import { scrubUrl } from 'app-components/utils/core/telemetrySafe';

const TELEMETRY_HOST = /(?:^|\.)(?:launchdarkly\.com|mixpanel\.com|pendo\.io)$/i;
const URL_KEYS = ['from', 'href', 'to', 'url'];
// Only what the SDK itself fills in. Breadcrumbs left by app code name their own keys.
const SDK_VALUE_KEYS: Partial<Record<Breadcrumb['type'], string[]>> = {
  error: ['errorMessage'],
  navigation: ['prevState', 'state', 'title'],
  user: ['targetSelector', 'targetText'],
};
// The tags the app renders. A closed list, because anything else can be a word from an id: see getClickedControl.
const ELEMENT_TAG =
  /^(?:A|ASIDE|B|BODY|BUTTON|CANVAS|CODE|DIV|EM|FIELDSET|FOOTER|FORM|H[1-6]|HR|HTML|I|IFRAME|IMG|INPUT|LABEL|LEGEND|LI|MAIN|NAV|OL|OPTION|P|PRE|SECTION|SELECT|SPAN|STRONG|TABLE|TBODY|TD|TEXTAREA|TH|THEAD|TR|UL|circle|g|line|path|polygon|rect|svg|use)(?=[#.:]|$)/;
// MuiButtonBase comes first on every kind of MUI button, so it says nothing the tag doesn't.
const MUI_CLASS = /\.((?!MuiButtonBase)Mui[A-Za-z]+-[A-Za-z]+)/;

// LaunchDarkly URLs hold the encoded user context in their path, so these go rather than get scrubbed.
const isTelemetryRequest = (url: unknown): boolean => {
  if (typeof url !== 'string') return false;
  const mixpanelProxy = getBuildConfig()?.mixpanelProxy;
  if (mixpanelProxy && url.startsWith(mixpanelProxy)) return true;
  return TELEMETRY_HOST.test(new URL(url, window.location.origin).hostname);
};

// A selector reads `DIV#page > BUTTON#save.MuiButton-root:nth-child(2)`, outermost first. Only the tag and a MUI class of
// what was clicked come from code. An id looks static but can be built from a name, like add-expense-button-groceries,
// and can even hold ` > ` itself, like qmenu_id_Home > REPAIRS, which makes the customer's word look like a tag.
const getClickedControl = (selector: unknown): string | undefined => {
  if (typeof selector !== 'string') return undefined;
  const clicked = selector.split(' > ').pop() ?? '';
  const tag = clicked.match(ELEMENT_TAG)?.[0];
  const muiClass = clicked.match(MUI_CLASS)?.[1];
  return tag && (muiClass ? `${tag}.${muiClass}` : tag);
};

/**
 * Takes customer data out of a breadcrumb before it enters the buffer. Returns false to drop it, including when
 * something goes wrong: a breadcrumb that couldn't be cleaned must not be sent.
 */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): boolean => {
  try {
    const { metadata, type } = breadcrumb;
    if (type === 'request' && isTelemetryRequest(metadata.url)) return false;
    const control = type === 'user' ? getClickedControl(metadata.targetSelector) : undefined;
    URL_KEYS.forEach((key) => {
      // history.pushState also takes a URL object.
      if (metadata[key] !== undefined && metadata[key] !== null) metadata[key] = scrubUrl(String(metadata[key]));
    });
    SDK_VALUE_KEYS[type]?.forEach((key) => {
      delete metadata[key];
    });
    if (control) metadata.control = control;
    return true;
  } catch {
    return false;
  }
};
